// Colorbar stack driver (Stage 2 — TASKS-UI.md).
//
// Subscribes to the binding registry and renders up to two colorbar
// entries (primary in the legacy `#colorbar-container` pane, secondary
// in `#colorbar-stack-secondary`). When the binding count exceeds two,
// the surplus shows up as click-to-cycle chips in `#colorbar-chip-row`.
//
// Install/dispose contract: returns a single disposer. All subscriptions
// and event listeners are tracked and torn down on dispose. See
// `src/ui/README.md` for the conventions.

import { updateColorbar } from '../colorbar';
import { sampleColormap as sampleColormapPure } from '@physics/colormap';
import { INTENTS } from '@store/intents';
import { mergeBindings } from '@store/bindings';
import { syncColormap } from '@app/colormapSync';
import { params } from '@store/instance';
import type { Store } from '@store/store';
import type {
    ColormapBinding,
    ColormapBindingId,
    State,
} from '@store/schema';
import type { ViewerContext } from '@app/context';

/**
 * Visible-slot resolution.
 *
 *   1. Build the active set from `bindingOrder` (creation order).
 *   2. Honor `visibleColorbarIds` first (user pins), then fill from
 *      `bindingOrder` until we have at most 2 slots.
 *   3. Remaining active ids become overflow chips.
 *
 * Bindings that aren't referenced by any primitive are *not* shown — the
 * intent handler GCs them on share, so this is defense in depth.
 */
interface StackLayout {
    slots: ReadonlyArray<ColormapBindingId>;       // up to 2
    overflow: ReadonlyArray<ColormapBindingId>;
    warnThird: boolean;  // visual cue when 3+ active bindings exist
}

function activeIds(state: State): ColormapBindingId[] {
    const p = state.params;
    const ref = new Set<ColormapBindingId>();
    for (const id of [p.slicesColormapId, p.volumeColormapId, p.contourColormapId, p.fieldlinesColormapId]) {
        if (id) ref.add(id);
    }
    // Preserve bindingOrder so the visual order is stable.
    return p.bindingOrder.filter((id) => ref.has(id));
}

function layoutFor(state: State): StackLayout {
    const active = activeIds(state);
    const pinned = state.params.visibleColorbarIds.filter((id) => active.includes(id));
    const slots: ColormapBindingId[] = [];
    for (const id of pinned) {
        if (slots.length < 2 && !slots.includes(id)) slots.push(id);
    }
    for (const id of active) {
        if (slots.length < 2 && !slots.includes(id)) slots.push(id);
    }
    const overflow = active.filter((id) => !slots.includes(id));
    return { slots, overflow, warnThird: active.length >= 3 };
}

function legacyBinding(state: State): ColormapBinding {
    const p = state.params;
    return {
        id: '__legacy__',
        field: p.fieldComponent || 'magnitude',
        colormap: p.colormap,
        min: p.colormapMin,
        max: p.colormapMax,
        log: !!p.colormapLog,
        logFloor: 1e-3,
        units: p.colormapUnits,
        centerOnZero: !!p.centerOnZero,
        transparentBounds: !!p.transparentBounds,
    };
}

export function installColorbarStack(store: Store<State>, viewer: ViewerContext): () => void {
    const primaryContainer = document.getElementById('colorbar-container');
    const primaryTarget = document.getElementById('colorbar');
    const secondaryContainer = document.getElementById('colorbar-stack-secondary');
    const secondaryTarget = document.getElementById('colorbar-secondary');
    const chipRow = document.getElementById('colorbar-chip-row');

    const sample = (name: string, t01: number): ReturnType<typeof sampleColormapPure> =>
        sampleColormapPure(viewer.colormap.defs, name, t01);

    const paintSlot = (b: ColormapBinding, target: HTMLElement, scope: HTMLElement): void => {
        updateColorbar({
            currentColormap: b.colormap,
            sampleColormap: sample,
            colormapMin: b.min,
            colormapMax: b.max,
            colormapLog: b.log,
            fieldMetadata: { ...(viewer.field.metadata ?? {}), field_units: b.units || undefined } as never,
            unitsMode: params.colorbarUnits,
            target,
            scope,
        });
    };

    const renderChips = (overflow: ReadonlyArray<ColormapBindingId>, layout: StackLayout): void => {
        if (!chipRow) return;
        // Tear down listeners on the prior chip set before rebuilding.
        chipRow.replaceChildren();
        if (!overflow.length) {
            chipRow.classList.add('is-hidden');
            chipRow.setAttribute('aria-hidden', 'true');
            return;
        }
        chipRow.classList.remove('is-hidden');
        chipRow.setAttribute('aria-hidden', 'false');
        const primaryId = layout.slots[0];
        for (const id of overflow) {
            const b = store.state.params.bindings[id];
            if (!b) continue;
            const wrap = document.createElement('span');
            wrap.className = 'colorbar-chip';
            // Warn border on overflow chips matches the spec — the binding
            // counts in the "soft warning" because >2 are active.
            wrap.classList.add('has-warn');
            wrap.title = `Click to show; ✕ merges into ${primaryId ?? 'primary'}`;
            wrap.setAttribute('data-binding-id', b.id);

            const showBtn = document.createElement('button');
            showBtn.type = 'button';
            showBtn.className = 'colorbar-chip-show';
            const swatch = document.createElement('span');
            swatch.className = 'colorbar-chip-swatch';
            const c0 = sample(b.colormap, 0.0);
            const c1 = sample(b.colormap, 1.0);
            swatch.style.background = `linear-gradient(to right, rgb(${c0.r},${c0.g},${c0.b}), rgb(${c1.r},${c1.g},${c1.b}))`;
            const label = document.createElement('span');
            label.className = 'colorbar-chip-label';
            label.textContent = b.field;
            showBtn.append(swatch, label);
            showBtn.addEventListener('click', () => onShowChip(b.id));

            wrap.appendChild(showBtn);

            if (primaryId && primaryId !== b.id) {
                const mergeBtn = document.createElement('button');
                mergeBtn.type = 'button';
                mergeBtn.className = 'colorbar-chip-merge';
                mergeBtn.setAttribute('aria-label', `Merge ${b.field} into primary colormap`);
                mergeBtn.title = `Merge ${b.field} → primary`;
                mergeBtn.textContent = '✕';
                mergeBtn.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    onMergeChip(b.id, primaryId);
                });
                wrap.appendChild(mergeBtn);
            }

            chipRow.appendChild(wrap);
        }
    };

    const onShowChip = (id: ColormapBindingId): void => {
        // Cycle: bump the clicked binding into the first pinned slot, push
        // existing pins down. Length cap at 2 so the layout stays small.
        const cur = store.state.params.visibleColorbarIds.filter((x) => x !== id);
        const next = [id, ...cur].slice(0, 2);
        store.dispatch(INTENTS.COLORMAP_SET_VISIBLE, { ids: next });
    };

    const onMergeChip = (sourceId: ColormapBindingId, targetId: ColormapBindingId): void => {
        mergeBindings(sourceId, targetId);
        // Repaint everything that depends on bindings (scenes + colorbars).
        // bindingOrder + bindings subscriptions already fire, but the scene
        // fan-out (slices/volume/contour) needs an explicit poke since the
        // primitive's *ColormapId stayed the same value for those still on
        // the target binding — only repointed primitives changed their id.
        syncColormap(viewer, params);
    };

    const render = (): void => {
        const state = store.state;
        const layout = layoutFor(state);

        // --- primary slot ---
        if (primaryTarget && primaryContainer) {
            const id = layout.slots[0];
            const b = (id && state.params.bindings[id]) ? state.params.bindings[id]! : legacyBinding(state);
            paintSlot(b, primaryTarget, primaryContainer);
            primaryContainer.classList.remove('is-hidden');
        }

        // --- secondary slot ---
        if (secondaryContainer && secondaryTarget) {
            const id = layout.slots[1];
            if (id) {
                const b = state.params.bindings[id];
                if (b) {
                    paintSlot(b, secondaryTarget, secondaryContainer);
                    secondaryContainer.classList.remove('is-hidden');
                    secondaryContainer.setAttribute('aria-hidden', 'false');
                    secondaryContainer.classList.toggle('has-warn', layout.warnThird);
                }
            } else {
                secondaryContainer.classList.add('is-hidden');
                secondaryContainer.setAttribute('aria-hidden', 'true');
                secondaryContainer.classList.remove('has-warn');
            }
        }

        // --- overflow chips ---
        renderChips(layout.overflow, layout);
    };

    const offs: Array<() => void> = [];
    offs.push(store.subscribe('params.bindings', render));
    offs.push(store.subscribe('params.bindingOrder', render));
    offs.push(store.subscribe('params.visibleColorbarIds', render));
    offs.push(store.subscribe('params.slicesColormapId', render));
    offs.push(store.subscribe('params.volumeColormapId', render));
    offs.push(store.subscribe('params.contourColormapId', render));
    offs.push(store.subscribe('params.fieldlinesColormapId', render));
    offs.push(store.subscribe('params.colorbarUnits', render));

    render();

    return () => {
        offs.forEach((off) => { try { off(); } catch { /* swallow */ } });
        // Best-effort DOM cleanup: chip row + secondary slot revert to hidden.
        if (chipRow) { chipRow.replaceChildren(); chipRow.classList.add('is-hidden'); }
        if (secondaryContainer) secondaryContainer.classList.add('is-hidden');
    };
}
